import React, { useCallback, useMemo } from 'react';
import { ReactComponent as CloseGrey } from '../assets/close-grey.svg';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../reducers';
import {
  ENTITY_TYPE,
  getActiveAccountsForCustomerOrders,
  getPaidAccountsForCustomerOrders,
  getPlanType,
} from './utils';
import {
  CustomerOrder,
  CustomerSuspensionParams,
} from '../model/customerSuspensionDetails';
import { ORDER_STATUSES } from '../helpers/constants';
import OrderStatusIndicator from './orderStatusIndicator';

interface CustomerDetailsProps {
  customerSuspensionOrderInfo: CustomerSuspensionParams;
}

interface ParentProps {
  closeModal: (
    entityType: (typeof ENTITY_TYPE)[keyof typeof ENTITY_TYPE],
    shouldRefreshData: boolean
  ) => void;
  handleProceedClick: () => void;
}

type Props = CustomerDetailsProps & ParentProps;

const { ACTIVE, PENDING, SUSPENDED, SUSPENDED_PENDING, DELETED } = ORDER_STATUSES;

const SuspendCustomerConfirmation: React.FC<Props> = ({
  customerSuspensionOrderInfo,
  closeModal,
  handleProceedClick,
}) => {
  // Retrieve orders from the customer suspension details
  const orders: CustomerOrder[] = useMemo(
    () => customerSuspensionOrderInfo.orderInfo.orders || [],
    [customerSuspensionOrderInfo]
  );

  const numberOfOrders = orders.length;

  // Compare function to sort orders
  const compareCustomerOrder = useCallback(
    (o1: CustomerOrder, o2: CustomerOrder) => {
      const orderStatusPriority = [ACTIVE, SUSPENDED, DELETED, PENDING, SUSPENDED_PENDING];
      const orderPlanPriority = [
        'premium',
        'premium_trial',
        'pro',
        'pro_trial',
        'free',
      ];
      const statusDiff =
        orderStatusPriority.indexOf(o1.status) -
        orderStatusPriority.indexOf(o2.status);
      if (statusDiff === 0) {
        if (
          (o1.plan.isPaid && !o2.plan.isPaid) ||
          (!o1.plan.isPaid && o2.plan.isPaid)
        ) {
          return o1.plan.isPaid ? -1 : 1;
        } else {
          return (
            orderPlanPriority.indexOf(o1.plan.planType) -
            orderPlanPriority.indexOf(o2.plan.planType)
          );
        }
      }
      return statusDiff;
    },
    []
  );

  // Sorted orders copy (avoid mutating original orders)
  const sortedOrders = useMemo(() => {
    const ordersCopy = [...orders];
    ordersCopy.sort(compareCustomerOrder);
    return ordersCopy;
  }, [orders, compareCustomerOrder]);

  // Generate table rows for each customer order
  const customerOrderComponents = useMemo(() => {
    return sortedOrders.map((order) => (
      <tr key={`${order.domainName}-${order.type}`}>
        <td className="customer-order-table-domain">
          <span className="customer-order-domain">{order.domainName}:</span>{' '}
          {order.type}
        </td>
        <td className="customer-order-table-plan customer-order-table-center-text">
          {getPlanType(order.plan.planType)}
        </td>
        <td className="customer-order-table-status">
          <OrderStatusIndicator status={order.status} />
        </td>
      </tr>
    ));
  }, [sortedOrders]);

  // Compute summary values
  const activeOrders = useMemo(
    () => getActiveAccountsForCustomerOrders(orders),
    [orders]
  );
  const paidOrders = useMemo(
    () => getPaidAccountsForCustomerOrders(orders),
    [orders]
  );

  return (
    <div>
      <CloseGrey
        className="alert-modal-close-button"
        onClick={() => closeModal(ENTITY_TYPE.CUSTOMER, false)}
      />
      <div className="customer-suspension-container">
        <div className="suspension-heading">
          <FormattedMessage id="suspend-customer" />
        </div>
        <div className="suspension-subheading">
          {customerSuspensionOrderInfo.orderInfo.email}
        </div>
        <div className="customer-suspension-order-summary">
          <div className="customer-suspension-order-summary-heading">
            <FormattedMessage id="orders-header" />
          </div>
          <div className="customer-suspension-order-summary-value">
            {numberOfOrders}
          </div>
          <div className="customer-suspension-order-summary-heading">
            <FormattedMessage id="active-orders-header" />
          </div>
          <div className="customer-suspension-order-summary-value">
            {activeOrders}
          </div>
          <div className="customer-suspension-order-summary-heading">
            <FormattedMessage id="paid-orders-header" />
          </div>
          <div className="customer-suspension-order-summary-value">
            {paidOrders}
          </div>
        </div>
        <div className="customer-suspension-order-table">
          <table>
            <thead>
              <tr>
                <th className="customer-order-table-domain">
                  <FormattedMessage id="suspend-customer-domain-name-header" />
                </th>
                <th className="customer-order-table-plan customer-order-table-center-text">
                  <FormattedMessage id="suspend-customer-plan-header" />
                </th>
                <th className="customer-order-table-status">
                  <FormattedMessage id="suspend-customer-status-header" />
                </th>
              </tr>
            </thead>
            <tbody>{customerOrderComponents}</tbody>
          </table>
        </div>
        <div className="customer-suspension-button-container">
          <button
            className="button button-primary"
            onClick={handleProceedClick}
          >
            <FormattedMessage id="suspend-customer-confirmation-button-text" />
          </button>
          <button
            className="button suspension-button modal-cancel-button"
            onClick={() => closeModal(ENTITY_TYPE.CUSTOMER, false)}
          >
            <FormattedMessage id="cancel" />
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  customerSuspensionOrderInfo: (state.orders as any)
    .customerSuspensionOrderInfo,
});

export default connect(mapStateToProps)(SuspendCustomerConfirmation);
